import Phaser from 'phaser';
import { COLORS, FONTS, toHexColor } from '../config/gameConfig';
import type { EventBus, Term } from '../systems/EventBus';
import { TEACH_PANELS } from '../content/teach-panels';
import type { MarkedTextSegment } from './autoMark';
import type { Tooltip } from './Tooltip';

export interface MarkedTextOptions {
  wrapWidth?: number;
}

export interface MarkedTextResult {
  width: number;
  height: number;
}

interface RenderedSegment {
  text: Phaser.GameObjects.Text;
  underline?: Phaser.GameObjects.Rectangle;
  hit?: Phaser.GameObjects.Rectangle;
  mark?: Term;
}

const splitToTokens = (s: string): string[] => {
  // Split on spaces while preserving them as their own tokens for layout.
  const tokens: string[] = [];
  let buf = '';
  for (const ch of s) {
    if (ch === ' ') {
      if (buf) { tokens.push(buf); buf = ''; }
      tokens.push(' ');
    } else {
      buf += ch;
    }
  }
  if (buf) tokens.push(buf);
  return tokens;
};

/** Render a sequence of segments inline with optional word-wrap. Marked
 *  segments get a dotted underline + hover/click affordances. */
export const renderMarkedText = (
  scene: Phaser.Scene,
  bus: EventBus,
  tooltip: Tooltip,
  container: Phaser.GameObjects.Container,
  startX: number,
  startY: number,
  segments: readonly MarkedTextSegment[],
  style: Phaser.Types.GameObjects.Text.TextStyle,
  options?: MarkedTextOptions,
): MarkedTextResult => {
  const wrapWidth = options?.wrapWidth ?? Infinity;
  const measureText = (t: string): Phaser.GameObjects.Text =>
    scene.add.text(0, 0, t, { fontFamily: FONTS.ui, ...style });

  const probe = measureText('M');
  const lineHeight = probe.height;
  probe.destroy();

  const rendered: RenderedSegment[] = [];
  let cursorX = 0;
  let cursorY = 0;
  let lineMaxRight = 0;

  for (const seg of segments) {
    const segColor = seg.mark
      ? toHexColor(COLORS.markedTermUnderline)
      : (style.color as string | undefined) ?? toHexColor(COLORS.textPrimary);

    const tokens = splitToTokens(seg.text);
    let pendingTokens = '';
    const flushPending = (): void => {
      if (!pendingTokens) return;
      const t = scene.add.text(startX + cursorX, startY + cursorY, pendingTokens, {
        fontFamily: FONTS.ui,
        ...style,
        color: segColor,
      });
      container.add(t);
      const segWidth = t.width;

      let underline: Phaser.GameObjects.Rectangle | undefined;
      let hit: Phaser.GameObjects.Rectangle | undefined;
      if (seg.mark) {
        underline = scene.add.rectangle(
          startX + cursorX,
          startY + cursorY + lineHeight - 1,
          segWidth,
          1,
          COLORS.markedTermUnderline,
        ).setOrigin(0, 0);
        container.add(underline);

        hit = scene.add.rectangle(
          startX + cursorX,
          startY + cursorY,
          segWidth,
          lineHeight,
          0x000000,
          0,
        ).setOrigin(0, 0);
        hit.setInteractive({ useHandCursor: true });
        const term = seg.mark;
        const tooltipText = TEACH_PANELS[term].definition;
        hit.on('pointerover', () => {
          tooltip.show(tooltipText, startX + cursorX + segWidth / 2, startY + cursorY);
        });
        hit.on('pointerout', () => tooltip.hide());
        hit.on('pointerdown', () => {
          tooltip.hide();
          bus.emit('field-guide:focus-term', { term });
        });
        container.add(hit);
      }

      rendered.push({ text: t, underline, hit, mark: seg.mark });
      cursorX += segWidth;
      lineMaxRight = Math.max(lineMaxRight, cursorX);
      pendingTokens = '';
    };

    for (const tok of tokens) {
      // Try adding token to pending; if would overflow wrap, flush+wrap first.
      const probe2 = scene.add.text(0, 0, pendingTokens + tok, {
        fontFamily: FONTS.ui,
        ...style,
      });
      const candidateWidth = probe2.width;
      probe2.destroy();
      if (cursorX + candidateWidth > wrapWidth && (cursorX > 0 || pendingTokens.length > 0)) {
        flushPending();
        if (tok === ' ') continue; // skip leading space on new line
        cursorX = 0;
        cursorY += lineHeight + 2;
      }
      pendingTokens += tok;
    }
    flushPending();
  }

  return { width: lineMaxRight, height: cursorY + lineHeight };
};
